import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { SyncProgressState } from '../components/SyncProgressModal';
import { autoDismissDelayMs } from '../utils/syncProgressPresenter';

/**
 * Every sync shows up in exactly one place, decided here and not by the producer: the
 * pinned card (components/SyncProgressCardSlot), which fades on its own like a toast but
 * never shares the toast stack's shuffling. Zooming promotes it to the full modal.
 * Producers just call `setData`.
 */

export interface SyncProgressContextValue {
  data: SyncProgressState | null;
  retryFn: (() => void) | null;
  isPaused: boolean;
  isStopped: boolean;
  /** True while the full modal is open; false means the card is the surface. */
  isExpanded: boolean;
  pauseRef: React.MutableRefObject<boolean>;
  stopRef: React.MutableRefObject<boolean>;
  setData: (data: SyncProgressState | null) => void;
  setRetryFn: (fn: (() => void) | null) => void;
  expand: () => void;
  collapse: () => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  close: () => void;
}

const SyncProgressContext = createContext<SyncProgressContextValue>({
  data: null,
  retryFn: null,
  isPaused: false,
  isStopped: false,
  isExpanded: false,
  pauseRef: { current: false },
  stopRef: { current: false },
  setData: () => {},
  setRetryFn: () => {},
  expand: () => {},
  collapse: () => {},
  pause: () => {},
  resume: () => {},
  stop: () => {},
  close: () => {},
});

export const SyncProgressProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [data, setDataState] = useState<SyncProgressState | null>(null);
  const [retryFn, setRetryFnState] = useState<(() => void) | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isStopped, setIsStopped] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const pauseRef = useRef(false);
  const stopRef = useRef(false);

  const setData = (d: SyncProgressState | null) => {
    if (d && !d.isComplete) {
      // New sync starting — reset control flags
      pauseRef.current = false;
      stopRef.current = false;
      setIsPaused(false);
      setIsStopped(false);
    }
    setDataState(d);
  };

  const setRetryFn = (fn: (() => void) | null) => {
    setRetryFnState(fn ? () => fn : null);
  };

  const pause = () => { pauseRef.current = true;  setIsPaused(true);  };
  const resume = () => { pauseRef.current = false; setIsPaused(false); };

  const stop = () => {
    stopRef.current = true;
    pauseRef.current = false;
    setIsStopped(true);
    setIsPaused(false);
    setDataState(prev => prev && !prev.isComplete ? { ...prev, isComplete: true } : prev);
  };

  const close = useCallback(() => {
    stopRef.current = true;
    pauseRef.current = false;
    setDataState(null);
    setRetryFnState(null);
    setIsPaused(false);
    setIsStopped(false);
    setIsExpanded(false);
    setTimeout(() => { stopRef.current = false; }, 300);
  }, []);

  // The modal takes over the card's slot; two live copies of one run was the original bug.
  const expand = useCallback(() => setIsExpanded(true), []);

  const collapse = useCallback(() => setIsExpanded(false), []);

  // A new run always starts on the card, even if the previous one was left zoomed: either
  // nothing was running, or this is a fresh run's opening frame (done === 0).
  const wasRunning = useRef(false);
  useEffect(() => {
    const running = !!data && !data.isComplete;
    if (running && (data!.done === 0 || !wasRunning.current)) setIsExpanded(false);
    wasRunning.current = running;
  }, [data]);

  // ── auto-dismiss, like any toast — but never while someone is reading the modal ──
  useEffect(() => {
    if (!data || isExpanded) return;
    const delay = autoDismissDelayMs({ isComplete: data.isComplete, fail: data.fail });
    if (delay === null) return;
    const timer = setTimeout(close, delay);
    return () => clearTimeout(timer);
  }, [data, isExpanded, close]);

  return (
    <SyncProgressContext.Provider
      value={{
        data, retryFn, isPaused, isStopped, isExpanded, pauseRef, stopRef,
        setData, setRetryFn, expand, collapse, pause, resume, stop, close,
      }}
    >
      {children}
    </SyncProgressContext.Provider>
  );
};

export const useSyncProgress = () => useContext(SyncProgressContext);
